import React, { useEffect, useMemo, useState } from "react";
import CourseSelect from "./CourseSelect";
import { cachedCatalog, fetchCatalog } from "./catalogSource";
import { Course } from "./types";
import { formatCourseLocation } from "./coursesDb";
import { ActiveGame } from "./activeRound";
import TournamentGamesStep from "./TournamentGamesStep";
import {
  TOURNAMENT_BET_GRID_IDS,
  TOURNAMENT_MANDATORY_IDS,
  tournamentDefaultOptionsFor,
} from "./tournamentGames";
import { RosterOwner, RosterPlayer, loadOwner } from "./roster";
import {
  MAX_GROUP_PLAYERS,
  Tournament,
  TournamentGroup,
  availableRoster,
  canScore,
  displayedGameNames,
  emptyTournament,
  isConnected,
  isGroupReady,
  isOwnerAssigned,
  newGroup,
  ownerToTournamentPlayer,
  rosterToTournamentPlayer,
} from "./tournament";

interface Props {
  onCancel: () => void;
  onComplete: (tournament: Tournament) => void;
  // When set, the wizard edits an existing tournament instead of creating one.
  // Same id flows back out so the dashboard can replace it in place.
  initial?: Tournament | null;
  // Save failure from the last submit. The wizard stays open with the draft
  // intact so it can be retried — a failed save must never look like a success.
  error?: string | null;
}

const STEPS = ["Details", "Course", "Groups", "Games", "Review"] as const;

const MANDATORY_GAME_IDS = TOURNAMENT_MANDATORY_IDS;

function initialGames(): ActiveGame[] {
  return MANDATORY_GAME_IDS.map((id) => ({
    id,
    enabled: true,
    options: tournamentDefaultOptionsFor(id),
  }));
}

// Middle tee on the course — matches createActiveRound / Players-modal auto-tee.
function middleTeeId(course: Course | null): string | undefined {
  const tees = course?.tees || [];
  return tees.length ? tees[Math.floor(tees.length / 2)].id : undefined;
}

export default function TournamentWizard({
  onCancel,
  onComplete,
  initial,
  error,
}: Props) {
  const [step, setStep] = useState(0); // 0-based index into STEPS
  const [draft, setDraft] = useState<Tournament>(() => {
    if (initial) {
      // Backfill a default tee for legacy/mock tournaments that predate the
      // tee feature so the per-player selectors land on something sensible.
      if (!initial.defaultTeeId && initial.course?.tees?.length) {
        const tee = middleTeeId(initial.course);
        return {
          ...initial,
          defaultTeeId: tee,
          groups: initial.groups.map((g) => ({
            ...g,
            players: g.players.map((p) => ({ ...p, tee: p.tee ?? tee })),
          })),
        };
      }
      return initial;
    }
    return { ...emptyTournament(), games: initialGames() };
  });
  const editing = !!initial;
  const owner = useMemo(() => loadOwner(), []);

  // When set, the add-player picker is open for this group id.
  const [pickerGroupId, setPickerGroupId] = useState<string | null>(null);

  // ---------- draft mutators ----------

  const patch = (p: Partial<Tournament>) => setDraft((d) => ({ ...d, ...p }));
  const setGroups = (groups: TournamentGroup[]) => patch({ groups });

  // Create a group and immediately point the picker at it — the only path to
  // a new group now (the "Add another group" action inside the player window).
  const addGroupAndPick = () => {
    const g = newGroup(draft.groups.length);
    setGroups([...draft.groups, g]);
    setPickerGroupId(g.id);
  };

  const removeGroup = (id: string) =>
    setGroups(draft.groups.filter((g) => g.id !== id));

  const renameGroup = (id: string, name: string) =>
    setGroups(draft.groups.map((g) => (g.id === id ? { ...g, name } : g)));

  const setGroupTeeTime = (id: string, teeTime: string) =>
    setGroups(draft.groups.map((g) => (g.id === id ? { ...g, teeTime } : g)));

  const addPlayerToGroup = (groupId: string, player: RosterPlayer) =>
    setGroups(
      draft.groups.map((g) =>
        g.id === groupId && g.players.length < MAX_GROUP_PLAYERS
          ? {
              ...g,
              players: [
                ...g.players,
                rosterToTournamentPlayer(player, draft.defaultTeeId),
              ],
            }
          : g,
      ),
    );

  const addOwnerToGroup = (groupId: string) => {
    if (!owner || isOwnerAssigned(draft.groups)) return;
    setGroups(
      draft.groups.map((g) =>
        g.id === groupId && g.players.length < MAX_GROUP_PLAYERS
          ? {
              ...g,
              players: [
                ...g.players,
                ownerToTournamentPlayer(owner, draft.defaultTeeId),
              ],
            }
          : g,
      ),
    );
  };

  const removePlayerFromGroup = (groupId: string, rosterId: string) =>
    setGroups(
      draft.groups.map((g) =>
        g.id === groupId
          ? { ...g, players: g.players.filter((p) => p.rosterId !== rosterId) }
          : g,
      ),
    );

  // Changing the default tee assigns it to everyone already added (the usual
  // "everyone plays the same tee" case); individual tees can still be tweaked
  // afterwards.
  const setDefaultTee = (teeId: string) =>
    setDraft((d) => ({
      ...d,
      defaultTeeId: teeId,
      groups: d.groups.map((g) => ({
        ...g,
        players: g.players.map((p) => ({ ...p, tee: teeId })),
      })),
    }));

  const setPlayerTee = (groupId: string, rosterId: string, tee: string) =>
    setGroups(
      draft.groups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              players: g.players.map((p) =>
                p.rosterId === rosterId ? { ...p, tee } : p,
              ),
            }
          : g,
      ),
    );

  const toggleGame = (id: string) => {
    if (MANDATORY_GAME_IDS.includes(id)) return;
    const on = draft.games.some((g) => g.id === id);
    patch({
      games: on
        ? draft.games.filter((g) => g.id !== id)
        : [
            ...draft.games,
            { id, enabled: true, options: tournamentDefaultOptionsFor(id) },
          ],
    });
  };

  // Set a single option on a game. Mirrors the player-side Nassau cascade for
  // bet-grid games: bumping the Overall bet (or first ticking Nassau) seeds the
  // front-9 / back-9 segment bets to match for one-tap setup.
  const setGameOption = (
    id: string,
    key: string,
    value: boolean | number | string,
  ) =>
    patch({
      games: draft.games.map((g) => {
        if (g.id !== id) return g;
        const nextOptions = { ...g.options, [key]: value };
        if (TOURNAMENT_BET_GRID_IDS.has(id)) {
          const nassauOn =
            key === "nassau" ? value === true : nextOptions.nassau === true;
          if (nassauOn && key === "overallBet") {
            nextOptions.frontNineBet = value;
            nextOptions.backNineBet = value;
          } else if (key === "nassau" && value === true) {
            const overall = nextOptions.overallBet;
            nextOptions.frontNineBet = overall;
            nextOptions.backNineBet = overall;
          }
        }
        return { ...g, options: nextOptions };
      }),
    });

  // ---------- per-step validity ----------

  const canAdvance = useMemo(() => {
    switch (step) {
      case 0:
        return draft.name.trim().length > 0 && !!draft.date;
      case 1:
        return !!draft.course;
      case 2: {
        // Empty groups are just "Add Players" placeholders — ignore them.
        // Need at least one real group, and every populated group ready.
        const populated = draft.groups.filter((g) => g.players.length > 0);
        return populated.length > 0 && populated.every(isGroupReady);
      }
      case 3:
        return true;
      default:
        return true;
    }
  }, [step, draft]);

  const goNext = () => {
    if (!canAdvance) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => {
    if (step === 0) onCancel();
    else setStep((s) => s - 1);
  };

  const finish = (status: Tournament["status"]) =>
    onComplete({
      ...draft,
      status,
      // Drop any empty "Add Players" placeholder groups before saving.
      groups: draft.groups.filter((g) => g.players.length > 0),
    });

  // ---------- render ----------

  return (
    <div className="cg-page">
      <div className="cg-subpage cg-wiz">
        <div className="cg-wiz-head">
          <h2 className="cg-wiz-title">
            {editing ? "Edit Tournament" : "New Tournament"}
          </h2>
          <button type="button" className="cg-btn cg-btn-ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>

        <ol className="cg-wiz-steps">
          {STEPS.map((label, i) => (
            <li
              key={label}
              className={
                "cg-wiz-step" +
                (i === step ? " cg-wiz-step-active" : "") +
                (i < step ? " cg-wiz-step-done" : "")
              }
            >
              <span className="cg-wiz-step-num">{i < step ? "✓" : i + 1}</span>
              <span className="cg-wiz-step-label">{label}</span>
            </li>
          ))}
        </ol>

        <div className="cg-wiz-body">
          {step === 0 && (
            <DetailsStep
              name={draft.name}
              date={draft.date}
              onName={(name) => patch({ name })}
              onDate={(date) => patch({ date })}
            />
          )}

          {step === 1 && (
            <CourseStep
              course={draft.course}
              onSelect={(course) => {
                patch({ course, defaultTeeId: middleTeeId(course) });
                setStep(2);
              }}
              onClear={() => patch({ course: null, defaultTeeId: undefined })}
              onContinue={() => setStep(2)}
            />
          )}

          {step === 2 && (
            <GroupsStep
              draft={draft}
              onAddGroup={addGroupAndPick}
              onRemoveGroup={removeGroup}
              onAddPlayer={(groupId) => setPickerGroupId(groupId)}
              onRemovePlayer={removePlayerFromGroup}
              onChangeDefaultTee={setDefaultTee}
              onChangePlayerTee={setPlayerTee}
            />
          )}

          {step === 3 && (
            <TournamentGamesStep
              games={draft.games}
              onToggle={toggleGame}
              onOptionChange={setGameOption}
            />
          )}

          {step === 4 && <ReviewStep draft={draft} />}
        </div>

        {error && (
          <div className="cg-wiz-error" role="alert">
            {error}
          </div>
        )}

        <div className="cg-wiz-foot">
          <button type="button" className="cg-btn cg-btn-ghost" onClick={goBack}>
            {step === 0 ? "Cancel" : "← Back"}
          </button>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              className="cg-btn cg-btn-primary"
              onClick={goNext}
              disabled={!canAdvance}
            >
              Next →
            </button>
          ) : editing ? (
            <button
              type="button"
              className="cg-btn cg-btn-primary"
              onClick={() => finish(draft.status)}
            >
              Save Changes
            </button>
          ) : (
            <div className="cg-wiz-finish">
              <button
                type="button"
                className="cg-btn"
                onClick={() => finish("draft")}
              >
                Save as Draft
              </button>
              <button
                type="button"
                className="cg-btn cg-btn-primary"
                onClick={() => finish("open")}
              >
                Create Tournament
              </button>
            </div>
          )}
        </div>
      </div>

      {pickerGroupId && (
        <PlayerPicker
          group={draft.groups.find((g) => g.id === pickerGroupId)!}
          pool={availableRoster(draft.groups)}
          owner={owner}
          ownerAssigned={isOwnerAssigned(draft.groups)}
          onAdd={(p) => addPlayerToGroup(pickerGroupId, p)}
          onAddOwner={() => addOwnerToGroup(pickerGroupId)}
          onRemove={(rosterId) => removePlayerFromGroup(pickerGroupId, rosterId)}
          onRename={(name) => renameGroup(pickerGroupId, name)}
          onChangeTeeTime={(t) => setGroupTeeTime(pickerGroupId, t)}
          onAddGroup={addGroupAndPick}
          onClose={() => setPickerGroupId(null)}
        />
      )}
    </div>
  );
}

// ───────────────────────── Step 1: Details ─────────────────────────

function DetailsStep({
  name,
  date,
  onName,
  onDate,
}: {
  name: string;
  date: string;
  onName: (v: string) => void;
  onDate: (v: string) => void;
}) {
  return (
    <div className="cg-wiz-pane">
      <label className="cg-field">
        <span>Tournament name</span>
        <input
          type="text"
          className="cg-search"
          placeholder="e.g. Saturday Medal"
          value={name}
          onChange={(e) => onName(e.target.value)}
          autoFocus
        />
      </label>
      <label className="cg-field">
        <span>Date</span>
        <input
          type="date"
          className="cg-search"
          value={date}
          onChange={(e) => onDate(e.target.value)}
        />
      </label>
    </div>
  );
}

// ───────────────────────── Step 2: Course ─────────────────────────

function CourseStep({
  course,
  onSelect,
  onClear,
  onContinue,
}: {
  course: Course | null;
  onSelect: (c: Course) => void;
  onClear: () => void;
  onContinue: () => void;
}) {
  // Shared catalog base (cached/bundled instantly, refreshed from the backend).
  const [catalog, setCatalog] = useState<Course[]>(cachedCatalog);
  useEffect(() => {
    let alive = true;
    fetchCatalog().then((c) => {
      if (alive) setCatalog(c);
    });
    return () => {
      alive = false;
    };
  }, []);
  if (course) {
    return (
      <div className="cg-wiz-pane">
        <div className="cg-wiz-selected-course">
          <div className="cg-course-name">{course.name}</div>
          {formatCourseLocation(course) && (
            <div className="cg-course-loc">{formatCourseLocation(course)}</div>
          )}
          <div className="cg-course-meta">
            {course.holes.length} holes • Par{" "}
            {course.holes.reduce((s, h) => s + h.par, 0)}
          </div>
        </div>
        <div className="cg-course-actions">
          <button
            type="button"
            className="cg-btn cg-btn-primary"
            onClick={onContinue}
          >
            Continue
          </button>
          <button type="button" className="cg-btn cg-btn-ghost" onClick={onClear}>
            Choose a different course
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="cg-wiz-pane">
      <CourseSelect
        courses={catalog}
        onSelect={onSelect}
        hideAddActions
      />
    </div>
  );
}

// ───────────────────────── Step 3: Groups & Players ─────────────────────────

function GroupsStep({
  draft,
  onAddGroup,
  onRemoveGroup,
  onAddPlayer,
  onRemovePlayer,
  onChangeDefaultTee,
  onChangePlayerTee,
}: {
  draft: Tournament;
  onAddGroup: () => void;
  onRemoveGroup: (id: string) => void;
  onAddPlayer: (groupId: string) => void;
  onRemovePlayer: (groupId: string, rosterId: string) => void;
  onChangeDefaultTee: (teeId: string) => void;
  onChangePlayerTee: (groupId: string, rosterId: string, tee: string) => void;
}) {
  const [teeInfoOpen, setTeeInfoOpen] = useState(false);
  const tees = draft.course?.tees || [];
  const populated = draft.groups.filter((g) => g.players.length > 0);
  const firstEmpty = draft.groups.find((g) => g.players.length === 0);

  // Empty-state entry: open the first empty group, or spin up a new one.
  const startAddPlayers = () =>
    firstEmpty ? onAddPlayer(firstEmpty.id) : onAddGroup();

  return (
    <div className="cg-wiz-pane">
      <p className="cg-subpage-note">
        Each group needs at least one <strong>Connected</strong> player from
        your Player List to keep score for that group.
      </p>

      {tees.length > 0 && populated.length > 0 && (
        <div className="cg-default-tee">
          <div className="cg-default-tee-row">
            <select
              className="cg-player-tee cg-default-tee-select"
              value={draft.defaultTeeId || ""}
              onChange={(e) => onChangeDefaultTee(e.target.value)}
              aria-label="Default tee"
            >
              {tees.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="cg-games-info-btn"
              onClick={() => setTeeInfoOpen((o) => !o)}
              aria-label="About the default tee"
              aria-expanded={teeInfoOpen}
              title="About the default tee"
            >
              i
            </button>
          </div>
          {teeInfoOpen && (
            <p className="cg-default-tee-note">
              Select Default tee's for the tournament, you can change for
              individuals once groups are assigned.
            </p>
          )}
        </div>
      )}

      {populated.map((g) => {
        const ready = isGroupReady(g);
        return (
          <div key={g.id} className="cg-group-card">
            <div className="cg-group-head">
              {/* Name + tee time are read-only here; edited in the modal. */}
              <div className="cg-group-title">
                <span className="cg-group-name-text">{g.name}</span>
                {g.teeTime && (
                  <span className="cg-group-teetime">{g.teeTime}</span>
                )}
              </div>
              {/* Opens the player manager (name, tee time, add / remove / swap). */}
              <button
                type="button"
                className="cg-group-edit"
                onClick={() => onAddPlayer(g.id)}
              >
                Edit
              </button>
              {populated.length > 1 && (
                <button
                  type="button"
                  className="cg-group-remove"
                  onClick={() => onRemoveGroup(g.id)}
                  aria-label="Remove group"
                >
                  ×
                </button>
              )}
            </div>

            <ul className="cg-group-players">
              {g.players.map((p) => (
                <li key={p.rosterId} className="cg-group-player">
                  <span className="cg-group-player-name">{p.name}</span>
                  {typeof p.handicapIndex === "number" && (
                    <span className="cg-group-player-hcp">{p.handicapIndex}</span>
                  )}
                  {p.isOwner ? (
                    <span className="cg-group-player-conn">You</span>
                  ) : isConnected(p) ? (
                    <span className="cg-group-player-conn">✓ Connected</span>
                  ) : (
                    <span className="cg-group-player-guest">Guest</span>
                  )}
                  {tees.length > 0 && (
                    <select
                      className="cg-player-tee cg-group-player-tee"
                      value={p.tee || draft.defaultTeeId || ""}
                      onChange={(e) =>
                        onChangePlayerTee(g.id, p.rosterId, e.target.value)
                      }
                      aria-label={`Tee for ${p.name}`}
                    >
                      {tees.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    type="button"
                    className="cg-group-player-remove"
                    onClick={() => onRemovePlayer(g.id, p.rosterId)}
                    aria-label={`Remove ${p.name}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>

            {!ready && (
              <p className="cg-group-warn">Needs a connected player</p>
            )}
          </div>
        );
      })}

      {populated.length === 0 ? (
        <div className="cg-add-players-wrap">
          <button
            type="button"
            className="cg-btn cg-btn-primary cg-add-players"
            onClick={startAddPlayers}
          >
            + Add Players
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="cg-btn cg-add-group"
          onClick={onAddGroup}
        >
          + Add Group
        </button>
      )}
    </div>
  );
}

// ───────────────────────── Player picker modal ─────────────────────────

function PlayerPicker({
  group,
  pool,
  owner,
  ownerAssigned,
  onAdd,
  onAddOwner,
  onRemove,
  onRename,
  onChangeTeeTime,
  onAddGroup,
  onClose,
}: {
  group: TournamentGroup;
  pool: RosterPlayer[];
  owner: RosterOwner | null;
  ownerAssigned: boolean;
  onAdd: (p: RosterPlayer) => void;
  onAddOwner: () => void;
  onRemove: (rosterId: string) => void;
  onRename: (name: string) => void;
  onChangeTeeTime: (teeTime: string) => void;
  onAddGroup: () => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const full = group.players.length >= MAX_GROUP_PLAYERS;
  const q = query.trim().toLowerCase();
  const filtered = q
    ? pool.filter((p) => p.name.toLowerCase().includes(q))
    : pool;
  // Connected players first — they can score for the group.
  const sorted = [...filtered].sort(
    (a, b) => Number(isConnected(b)) - Number(isConnected(a)),
  );
  // Offer "You" at the top until the admin has been placed in a group.
  const showOwner =
    !!owner && !ownerAssigned && (!q || owner.name.toLowerCase().includes(q));

  return (
    <div className="cg-modal-backdrop" onClick={onClose}>
      <div className="cg-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cg-modal-head">
          <div className="cg-modal-head-text">
            <h3>{group.name}</h3>
            <p className="cg-modal-subtitle">
              {group.players.length}/{MAX_GROUP_PLAYERS} players
            </p>
          </div>
          <button type="button" className="cg-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="cg-modal-body">
          {/* Group name + tee time — edited here, shown read-only on the card. */}
          <div className="cg-picker-fields">
            <label className="cg-field cg-picker-name">
              <span>Group name</span>
              <input
                type="text"
                className="cg-search"
                value={group.name}
                onChange={(e) => onRename(e.target.value)}
              />
            </label>
            <label className="cg-field cg-picker-teetime">
              <span>Tee time</span>
              <input
                type="text"
                className="cg-search"
                placeholder="e.g. 2:10"
                value={group.teeTime || ""}
                onChange={(e) => onChangeTeeTime(e.target.value)}
              />
            </label>
          </div>

          {/* Current members — removable so a player can be swapped out, even
              when the group is full. */}
          {group.players.length > 0 && (
            <ul className="cg-picker-members">
              {group.players.map((p) => (
                <li key={p.rosterId} className="cg-group-player">
                  <span className="cg-group-player-name">{p.name}</span>
                  {p.isOwner ? (
                    <span className="cg-group-player-conn">You</span>
                  ) : isConnected(p) ? (
                    <span className="cg-group-player-conn">✓ Connected</span>
                  ) : (
                    <span className="cg-group-player-guest">Guest</span>
                  )}
                  <button
                    type="button"
                    className="cg-group-player-remove"
                    onClick={() => onRemove(p.rosterId)}
                    aria-label={`Remove ${p.name}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {full ? (
            <p className="cg-modal-note">
              This group is full ({MAX_GROUP_PLAYERS}). Remove a player above to
              swap someone in, or add another group below.
            </p>
          ) : (
            <>
              <input
                className="cg-search"
                type="text"
                placeholder="Search your player list…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
              <ul className="cg-connect-results">
                {showOwner && (
                  <li>
                    <button
                      type="button"
                      className="cg-connect-result"
                      onClick={onAddOwner}
                    >
                      <span className="cg-connect-result-main">
                        <span className="cg-connect-result-name">
                          {owner!.name} (You)
                        </span>
                        {owner!.homeCourse && (
                          <span className="cg-connect-result-course">
                            {owner!.homeCourse}
                          </span>
                        )}
                      </span>
                      <span className="cg-group-player-conn">You</span>
                    </button>
                  </li>
                )}
                {sorted.length === 0 && !showOwner ? (
                  <p className="cg-modal-note">
                    {pool.length === 0
                      ? "Everyone in your player list is already assigned."
                      : `No players match "${query}".`}
                  </p>
                ) : (
                  sorted.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="cg-connect-result"
                        onClick={() => onAdd(p)}
                      >
                        <span className="cg-connect-result-main">
                          <span className="cg-connect-result-name">
                            {p.name}
                          </span>
                          {p.linkedHomeCourse && (
                            <span className="cg-connect-result-course">
                              {p.linkedHomeCourse}
                            </span>
                          )}
                        </span>
                        {isConnected(p) ? (
                          <span className="cg-group-player-conn">
                            ✓ Connected
                          </span>
                        ) : (
                          <span className="cg-group-player-guest">Guest</span>
                        )}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </>
          )}
        </div>
        <div className="cg-modal-foot">
          <button
            type="button"
            className="cg-btn cg-btn-ghost"
            onClick={onAddGroup}
          >
            + Add another group
          </button>
          <button
            type="button"
            className="cg-btn cg-btn-primary"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── Step 5: Review ─────────────────────────

function ReviewStep({ draft }: { draft: Tournament }) {
  const gameNames = displayedGameNames(draft.games);
  return (
    <div className="cg-wiz-pane cg-review">
      <div className="cg-review-block">
        <div className="cg-review-label">Tournament</div>
        <div className="cg-review-value">{draft.name || "—"}</div>
        <div className="cg-review-sub">{draft.date || "No date set"}</div>
      </div>

      <div className="cg-review-block">
        <div className="cg-review-label">Course</div>
        <div className="cg-review-value">{draft.course?.name || "—"}</div>
        {draft.course && formatCourseLocation(draft.course) && (
          <div className="cg-review-sub">
            {formatCourseLocation(draft.course)}
          </div>
        )}
      </div>

      <div className="cg-review-block">
        <div className="cg-review-label">Groups ({draft.groups.length})</div>
        {draft.groups.map((g) => (
          <div key={g.id} className="cg-review-group">
            <div className="cg-review-group-name">
              {g.name}
              {g.teeTime ? ` · ${g.teeTime}` : ""}
            </div>
            <div className="cg-review-group-players">
              {g.players.length === 0
                ? "No players"
                : g.players
                    .map((p) => p.name + (canScore(p) ? " ✓" : ""))
                    .join(", ")}
            </div>
          </div>
        ))}
      </div>

      <div className="cg-review-block">
        <div className="cg-review-label">Games</div>
        <div className="cg-review-value cg-review-games">
          {gameNames.join(", ")}
        </div>
      </div>
    </div>
  );
}
